import { Alert, Box, Button, Card, Group, Progress, Stack, Text, Tooltip } from "@mantine/core";
import { IconAlertTriangle, IconLock } from "@tabler/icons-react";
import { useState, type ReactNode } from "react";
import { fmtNumber, foodIconUrl, PassioApiError, type Feature } from "@passio/api-client";
import { EasyAIError } from "@passio/easyai-client";
import { useSession } from "../lib/session";
import { useUsage } from "../lib/usage";
import { openInspector } from "../lib/calls";
import { RequestIdChip } from "./RequestId";
import { AccountButton } from "./AccountCta";

export function UsageMeter({ dark }: { dark?: boolean }) {
  const u = useUsage();
  const pct = u.tokensPerDay > 0 ? Math.min(100, (u.tokensToday / u.tokensPerDay) * 100) : 0;
  const color = pct >= 90 ? "red" : pct >= 70 ? "orange" : "lime";
  return (
    <Tooltip label={u.tokensPerDay > 0 ? `${fmtNumber(u.tokensToday)} of ${fmtNumber(u.tokensPerDay)} tokens used today (resets midnight UTC)` : `${fmtNumber(u.tokensToday)} tokens today · no daily cap`}>
      <Stack gap={4} w={150}>
        <Group justify="space-between" gap={4}>
          <Text fz={11} ff="monospace" c={dark ? "var(--pd-ink-muted)" : "dimmed"}>
            tokens today
          </Text>
          <Text fz={11} ff="monospace" c={dark ? "white" : undefined}>
            {u.tokensPerDay > 0 ? `${Math.round(pct)}%` : fmtNumber(u.tokensToday)}
          </Text>
        </Group>
        <Progress value={u.tokensPerDay > 0 ? pct : 0} color={color} size="sm" bg={dark ? "var(--pd-ink-border)" : undefined} />
      </Stack>
    </Tooltip>
  );
}

const LIMIT_HINTS: Record<string, string> = {
  limit_user_tokens: "You've used today's token allowance for this demo account.",
  limit_ip_tokens: "This network has used today's allowance.",
  limit_user_rpm: "You're sending requests quickly. Wait a few seconds.",
  limit_ip_rpm: "Lots of requests from this network. Wait a minute.",
  feature_disabled: "Ask your Passio contact to enable this feature.",
  nutrition_not_configured: "The server has no Passio API key configured.",
};

/** Demo limits a real account lifts. */
const ACCOUNT_CODES = new Set(["limit_user_tokens", "limit_ip_tokens", "feature_disabled"]);

/** Friendly error with a link into the inspector. */
export function ErrorNotice({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (!error) return null;
  const e = error as PassioApiError | EasyAIError | Error;
  const code = e instanceof PassioApiError ? e.code : undefined;
  const status = e instanceof PassioApiError || e instanceof EasyAIError ? e.status : undefined;
  return (
    <Alert color={status === 429 ? "orange" : "red"} icon={<IconAlertTriangle size={18} />} title={status ? `Request failed (${status || "network"})` : "Something went wrong"}>
      <Stack gap={6}>
        <Text fz="sm">{e.message}</Text>
        {code && LIMIT_HINTS[code] && (
          <Text fz="sm" c="dimmed">
            {LIMIT_HINTS[code]}
          </Text>
        )}
        {code && ACCOUNT_CODES.has(code) && (
          <Text fz="sm">Need more than the demo allows? Your own Passio account has your plan's limits and every feature.</Text>
        )}
        <Group gap="xs">
          {code && ACCOUNT_CODES.has(code) && <AccountButton placement="limit" size="xs" />}
          {e instanceof PassioApiError && (
            <Button size="xs" variant="default" onClick={() => openInspector(null)}>
              Inspect call
            </Button>
          )}
          {onRetry && (
            <Button size="xs" variant="light" color="ink" onClick={onRetry}>
              Try again
            </Button>
          )}
        </Group>
      </Stack>
    </Alert>
  );
}

export function RequireFeature({ feature, children }: { feature: Feature; children: ReactNode }) {
  const { hasFeature, me } = useSession();
  if (hasFeature(feature)) return <>{children}</>;
  const label = me?.app.features.find((f) => f.value === feature)?.label ?? feature;
  return (
    <Card maw={560} mx="auto" mt="xl" ta="center">
      <Stack align="center" gap="sm">
        <IconLock size={32} />
        <Text fw={700} fz="lg">
          {label} isn't enabled for your account
        </Text>
        <Text c="dimmed">Your demo access includes a subset of features. Ask your Passio contact if you'd like to try this one, or create your own Passio account to use it with your key.</Text>
        {me?.user.authKind !== "api_key" && <AccountButton placement="locked" />}
      </Stack>
    </Card>
  );
}

/** Route guard for pages an admin can switch off (Admin → Tools). */
export function RequireTool({ tool, children }: { tool: string; children: ReactNode }) {
  const { toolEnabled } = useSession();
  if (toolEnabled(tool)) return <>{children}</>;
  return (
    <Card maw={560} mx="auto" mt="xl" ta="center">
      <Stack align="center" gap="sm">
        <IconLock size={32} />
        <Text fw={700} fz="lg">
          This tool isn't available right now
        </Text>
        <Text c="dimmed">It has been switched off for this demo. Pick another tool from the menu.</Text>
      </Stack>
    </Card>
  );
}

/** Passio food icon with a graceful fallback (not every iconId has an image). */
export function FoodIcon({ iconId, src, size = 44, alt = "" }: { iconId?: string; src?: string; size?: number; alt?: string }) {
  const [failed, setFailed] = useState(false);
  const url = src || foodIconUrl(iconId, size > 60 ? 180 : 90);
  if (!url || failed) {
    return (
      <Box className="pd-food-icon" w={size} h={size} style={{ display: "grid", placeItems: "center", color: "var(--mantine-color-dimmed)", fontSize: size * 0.4 }} aria-hidden>
        🍽
      </Box>
    );
  }
  return <img className="pd-food-icon" src={url} alt={alt} width={size} height={size} style={{ width: size, height: size }} onError={() => setFailed(true)} loading="lazy" />;
}

/** Title row above results: count, token usage, feedback and an inspector shortcut. */
export function ResultHeader({ title, count, meta, feedback }: { title: string; count?: number; meta?: { requestId?: string; requestUsage?: number }; feedback?: ReactNode }) {
  return (
    <Group justify="space-between" mb="sm" wrap="wrap" gap="xs">
      <Group gap="xs">
        <Text fw={750} fz="lg">
          {title}
        </Text>
        {count !== undefined && (
          <Text c="dimmed" fz="sm">
            {count} result{count === 1 ? "" : "s"}
          </Text>
        )}
        {meta?.requestUsage !== undefined && (
          <Text c="dimmed" fz="xs" ff="monospace">
            · {fmtNumber(meta.requestUsage)} tokens
          </Text>
        )}
        <RequestIdChip id={meta?.requestId} />
      </Group>
      <Group gap="xs">
        {feedback}
        <Button size="compact-sm" variant="subtle" color="gray" onClick={() => openInspector(null)}>
          Inspect
        </Button>
      </Group>
    </Group>
  );
}

export function EmptyState({ title, children, icon }: { title: string; children?: ReactNode; icon?: ReactNode }) {
  return (
    <Card ta="center" py="xl" bg="transparent" style={{ borderStyle: "dashed" }}>
      <Stack align="center" gap={6}>
        {icon}
        <Text fw={650}>{title}</Text>
        {children && (
          <Text c="dimmed" fz="sm" maw={460}>
            {children}
          </Text>
        )}
      </Stack>
    </Card>
  );
}
